import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Stack, CardMedia, Typography, Button, Card } from "@mui/material";
import Price from "@/components/Price";
import ScheduleAvailability from "@/components/ScheduleAvailability";
import ProductDetails from "@/features/product-details/ProductDetails";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { Content, OrderItem } from "@/types";
import { getThumbnailImage } from "@/utils/media";
import { sanitizeNumber } from "@/utils/numbers";
import { getOrderItemPrice } from "@/utils/order";
import { setSelectedMenuItem } from "@/store/uiSlice";
import { isAvailableToday } from "@/utils/weekDays";

const DishItemContent = () => {
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const content = useAppSelector((store) => store.organizations[organizationId]?.content);
  const categories = useAppSelector((store) => store.categories);
  const orgCategories = useAppSelector((store) => store.organizations[organizationId]?.categories);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const branchId = useAppSelector((store) => store.ui.activeBranch);

  const combinedCategories = { ...categories, ...orgCategories };

  // filter by isPublished true
  const publishedDishes = content?.filter((item: Content) => item.status === "published") || [];

  const dishes = useMemo(() => {
    return publishedDishes?.filter((item: Content) => {
      if (item.isDisabled) return false;
      if (branchId && item.branchSettings?.[branchId]?.isDisabled) return false;
      return true;
    }) || [];
  }, [publishedDishes, branchId]);

  //group dishes by category
  const groupedDishes: Record<string, Content[]> = dishes.reduce(
    (acc: Record<string, Content[]>, dish: Content) => {
      const category = Array.isArray(dish.category) ? String(dish.category[0] ?? "Uncategorized") : String(dish.category ?? "Uncategorized");
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(dish);
      return acc;
    },
    {} as Record<string, Content[]>,
  );

  const handleOpenDetails = (dish: Content) => {
    const searchParams = new URLSearchParams(location.search);

    searchParams.set("itemId", String(dish.id));

    navigate(`${location.pathname}?${searchParams.toString()}`);

    dispatch(setSelectedMenuItem(dish));
  };


  return (
    <>

      {Object.entries(groupedDishes).map(([category, items]) => {
        const categoryName = Object.values(combinedCategories)?.find((cat: any) => String(cat.id) === String(category))?.title;
        return (
          <Stack key={category}>
            <Typography variant="h3" fontWeight={500} color="textPrimary">
              {categoryName}
            </Typography>
            {items?.map((dish: Content, index: number) => {
              const dishId = dish?.id ?? dish?.title;

              const isFirst = index === 0;
              const isLast = index === items.length - 1;
              const isSingle = items.length === 1;
              return (
                <Card
                  key={dishId}
                  sx={{
                    width: "100%",
                    maxHeight: "75px",
                    borderRadius: isSingle ? 2 : isFirst ? "8px 8px 0 0" : isLast ? "0 0 8px 8px" : "0px",
                    borderBottom: isLast ? "none" : "1px solid #e0e0e0",
                    boxShadow: "none",
                  }}
                >
                  <Stack height={"100%"} direction={"row"} width={"100%"} spacing={3} alignItems={"center"}>
                    <Stack direction="row" alignItems="center" justifyContent={"space-between"} spacing={2} flex={1}>
                      <CardMedia
                        component="img"
                        image={getThumbnailImage({ file: dish?.images, organizationId, size: "800x500" })}
                        alt={dish?.title}
                        sx={{
                          width: "125px",
                          height: "100%",
                          borderRadius: isSingle || isFirst ? "8px 0 0 0" : 0,
                        }}
                      />
                      <Stack
                        flex={1}
                        sx={{
                          height: "100%",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="h4" fontWeight={500} color="textPrimary">
                          {dish?.title}
                        </Typography>
                        {dish.schedule ? <ScheduleAvailability availability={dish?.schedule} /> : null}
                      </Stack>
                      <Typography variant="body1">
                        <Price currency="NIO">{dish?.price != 0 ? getOrderItemPrice(dish as unknown as OrderItem) : sanitizeNumber(dish?.startingPrice)}</Price>
                      </Typography>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={2}
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        minWidth: 100,
                        pr: 2,
                      }}
                    >
                      {!isAvailableToday(dish?.schedule) || dish?.isDisabled ? (
                        <Button fullWidth color="primary" variant="outlined" onClick={() => handleOpenDetails(dish)}>
                          Ver
                        </Button>
                      ) : (
                        <Button fullWidth color="primary" onClick={() => handleOpenDetails(dish)}>
                          Añadir
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </Card>
              );
            })}
          </Stack>
        );
      })}

      <ProductDetails />
    </>
  );
};

export default DishItemContent;
